/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-runloop, ember/require-tagless-components */
import { classNames } from '@ember-decorators/component';
import Component from '@ember/component';
import { scheduleOnce } from '@ember/runloop';

// D3 imports
import { select } from 'd3-selection';
import { scaleOrdinal } from 'd3-scale';
import {
  schemeCategory10,
  schemePastel2,
  schemeSet3,
} from 'd3-scale-chromatic';
import { arc, pie } from 'd3-shape';
import tip from '@linc-technologies/linced-common/utils/d3-tip';

@classNames(
  'chart',
  'chart-pie',
  'layout-gt-xs-row',
  'layout-column',
  'layout-align-space-around-center',
)
export default class ChartPie extends Component {
  width = 320;
  height = 320;
  showLegend = true;
  showTooltip = true;
  showLabels = true;
  scrollLegend = true;
  data = [];
  sliceClicked() {}

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // Schedule a call to our `draw` method on Ember's "render" queue, which will
    // happen after the component has been placed in the DOM, and subsequently
    // each time data is changed.
    scheduleOnce('render', this, this.draw);
  }

  draw() {
    let plot = select(this.element.querySelector('svg'));
    let legendElement = this.element.querySelector('.legend');
    let legend = select(legendElement);
    let data = this.data;
    let width = this.width;
    let height = this.height;
    let color = scaleOrdinal(
      schemeCategory10.concat(schemePastel2).concat(schemeSet3),
    );

    // If we're showing a legend, remove its width from the available width so that we don't overlap.
    if (this.showLegend) {
      width -= legendElement.clientWidth;
      plot.attr('width', width);
    }

    // Calculate radius after adjusting width to account for legend.
    let radius = Math.min(width, height) / 2;

    // Remove previous chart (if there is one), then append the new root element.
    plot.selectAll('*').remove();
    let g = plot
      .append('g')
      .attr('transform', 'translate(' + width / 2 + ',' + height / 2 + ')');

    let chart = pie()
      .sort(null)
      .value((d) => d.count);

    let path = arc()
      .outerRadius(radius - 10)
      .innerRadius(0);

    let label = arc()
      .outerRadius(radius - 40)
      .innerRadius(radius - 40);

    // Tooltip
    let tooltip = tip()
      .attr('class', 'd3-tip')
      .html((d) => d.data.tooltip || d.data.label);
    g.call(tooltip);

    let pieArc = g
      .selectAll('.arc')
      .data(chart(data))
      .enter()
      .append('g')
      .attr('class', 'arc')
      .on('mouseover', tooltip.show)
      .on('mouseout', tooltip.hide)
      .on('click', (_, d) => this.sliceClicked(d.data));

    pieArc
      .append('path')
      .attr('d', path)
      .attr('fill', (d) => d.data.color || color(d.data.value));

    // Legend
    if (this.showLegend) {
      legend.selectAll('li').remove();
      let legendItem = legend
        .selectAll('li')
        .data(chart(data))
        .enter()
        .append('li');

      legendItem
        .append('span')
        .attr('class', 'legend-chip')
        .attr(
          'style',
          (d) =>
            `box-shadow: inset 0px 0px 0px 10px ${
              d.data.color || color(d.data.value)
            }`,
        );

      legendItem.append('span').text((d) => d.data.label);
    } else if (this.showLabels) {
      pieArc
        .append('text')
        .attr('transform', (d) => 'translate(' + label.centroid(d) + ')')
        .attr('dy', '0.35em')
        .text((d) => d.data.label);
    }
  }
}
